import React, { useState, useEffect, useRef } from "react"
import { toast } from "sonner"
import { DatePicker } from "@/components/ui/date-picker"
import { CompanyDetailSidebar, ProductDetailSidebar, VariantDetailSidebar, ExternalSellerDetailSidebar } from "@/components/dashboard/shared/SourcingDetailSidebars"
import { cn } from "@/lib/utils"
import {
  FileText,
  Loader2,
  Building2,
  Package,
  Calendar,
  AlertCircle,
  MapPin,
  Plus,
  X,
  CheckCircle2,
  ChevronLeft,
  ChevronDown,
  SlidersHorizontal,
  Check,
  Layers,
  FileCheck2,
  CreditCard,
  Hammer,
  Eye,
  FilePlus,
  ShoppingBag,
  Truck,
  Tag,
  Hash,
  MoreVertical
} from "lucide-react"
import { useAuthStore } from "@/store/auth.store"
import { useGetRfqBuyerToAdminById } from "@/hooks/buyer/useGetRfqBuyerToAdminById"
import { useGetRfqAdminToSellerById } from "@/hooks/admin/useGetRfqAdminToSellerById"
import { useGetRfqBuyerToAdminStatusUpdates } from "@/hooks/buyer/useGetRfqBuyerToAdminStatusUpdates"
import { useForwardRfq } from "@/hooks/admin/useForwardRfq"
import { useSendQuotationToBuyer } from "@/hooks/admin/useSendQuotationToBuyer"
import { useAddCompanyToRfq } from "@/hooks/admin/useAddCompanyToRfq"
import { useGetCompaniesByRfqId } from "@/hooks/admin/useGetCompaniesByRfqId"
import { searchCompanies } from "@/lib/api/company.api"
import { useNavigate } from "@tanstack/react-router"

function formatDate(isoStr: string) {
  if (!isoStr) return "N/A"
  return new Date(isoStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}

function formatDateTime(isoStr: string | null | undefined) {
  if (!isoStr) return "-"
  const d = new Date(isoStr)
  if (isNaN(d.getTime())) return "-"
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

import { TargetItemView } from "@/components/dashboard/shared/TargetItemView"
import { RfqStatusUpdatesTimeline } from "@/components/dashboard/shared/RfqStatusUpdatesTimeline"

export const MIN_QUANTITY = 1
export const MAX_QUANTITY = 100_000_000 // 100 Million

export const MIN_BUDGET = 1
export const MAX_BUDGET = 1_000_000_000 // 1 Billion

export function getQuantityError(val: number | ""): string | null {
  if (val === "") return null
  const num = Number(val)
  if (isNaN(num) || num < MIN_QUANTITY) {
    return `Quantity must be at least ${MIN_QUANTITY}`
  }
  if (num > MAX_QUANTITY) {
    return `Quantity cannot exceed ${MAX_QUANTITY.toLocaleString()}`
  }
  return null
}

export function getBudgetError(val: string): string | null {
  if (val === "") return null
  const num = Number(val)
  if (isNaN(num) || num < MIN_BUDGET) {
    return `Budget must be at least ${MIN_BUDGET}`
  }
  if (num > MAX_BUDGET) {
    return `Budget cannot exceed ${MAX_BUDGET.toLocaleString()}`
  }
  return null
}

export interface ChargeItem {
  description: string;
  unitPrice: number | string;
  discount: number | string;
  quantity: number | string;
  tax: number | string;
  taxType: string;
  productVariantId?: string;
}

const DEFAULT_CHARGE: ChargeItem = {
  description: "Shipping Fee, Customs",
  unitPrice: "",
  discount: "0",
  quantity: "1",
  tax: "18",
  taxType: "GST",
  productVariantId: ""
};

export function getChargeDescriptionError(val: string): string | null {
  if (val.length > 50) {
    return "Max 50 characters"
  }
  return null
}

export function getChargeUnitPriceError(val: string | number | ""): string | null {
  if (val === "") return null
  const str = String(val)
  if (str.includes(".") && str.split(".")[1]?.length > 2) {
    return "Max 2 decimals"
  }
  const num = Number(val)
  if (isNaN(num) || num <= 0) {
    return "Must be > 0"
  }
  if (num > 10_000_000) {
    return "Max 10,000,000"
  }
  return null
}

export function getChargeQuantityError(val: string | number | ""): string | null {
  if (val === "") return null
  const str = String(val)
  if (str.includes(".")) {
    return "Must be integer"
  }
  const num = Number(val)
  if (isNaN(num) || num <= 0 || !Number.isInteger(num)) {
    return "Must be > 0"
  }
  if (num >= 100_000) {
    return "Max 100,000"
  }
  return null
}

export function getChargeDiscountError(val: string | number | ""): string | null {
  if (val === "" || val === 0 || val === "0") return null
  const str = String(val)
  if (str.includes(".") && str.split(".")[1]?.length > 2) {
    return "Max 2 decimals"
  }
  const num = Number(val)
  if (isNaN(num) || num < 0 || num >= 100) {
    return "Must be 0-99.99%"
  }
  return null
}

export function getChargeTaxError(val: string | number | ""): string | null {
  if (val === "" || val === 0 || val === "0") return null
  const str = String(val)
  if (str.includes(".") && str.split(".")[1]?.length > 2) {
    return "Max 2 decimals"
  }
  const num = Number(val)
  if (isNaN(num) || num < 0 || num >= 100) {
    return "Must be 0-99.99%"
  }
  return null
}

export function isChargeRowCompleteAndValid(charge: ChargeItem): boolean {
  if (!charge.description.trim()) return false
  if (getChargeDescriptionError(charge.description) !== null) return false
  if (charge.unitPrice === "" || isNaN(Number(charge.unitPrice))) return false
  if (getChargeUnitPriceError(charge.unitPrice) !== null) return false
  if (charge.quantity === "" || isNaN(Number(charge.quantity))) return false
  if (getChargeQuantityError(charge.quantity) !== null) return false
  if (getChargeDiscountError(charge.discount) !== null) return false
  if (getChargeTaxError(charge.tax) !== null) return false
  return true
}

function RfqDetailView({
  rfq,
  activeTab,
  onBack,
  onForwardSuccess
}: {
  rfq: any
  activeTab: "buyers" | "sellers"
  onBack: () => void
  onForwardSuccess: (msg: string) => void
}) {
  const navigate = useNavigate()
  const token = useAuthStore((s) => s.accessToken)
  const rfqStatus = (rfq.status || "").toUpperCase()
  const isExpired = rfqStatus === "OPEN" && rfq.validUntil && new Date(rfq.validUntil) < new Date()
  const resolvedStatus = isExpired ? "EXPIRED" : (rfqStatus === "CLOSE" ? "CLOSED" : rfqStatus || "OPEN")
  const isOpen = resolvedStatus === "OPEN"

  const {
    data: statusUpdatesData,
    isLoading: isStatusUpdatesLoading,
  } = useGetRfqBuyerToAdminStatusUpdates((activeTab === "buyers" && rfq?.id) ? rfq.id : "")

  const adminToSellerList = rfq.adminToSellerRFQs
  const displayMessage = (Array.isArray(adminToSellerList) && adminToSellerList.length > 0)
    ? adminToSellerList[0].queryMessage
    : rfq.queryMessage

  const [editedMessage, setEditedMessage] = useState("")
  const [status, setStatus] = useState("OPEN")
  const [validUntil, setValidUntil] = useState("")
  const [canSellerAccess, setCanSellerAccess] = useState(true)
  const [country, setCountry] = useState("")
  const [address, setAddress] = useState("")
  const [moq, setMoq] = useState("")
  const [budget, setBudget] = useState("")
  const [budgetCurrency, setBudgetCurrency] = useState("")
  const [isBudgetCurrencyDropdownOpen, setIsBudgetCurrencyDropdownOpen] = useState(false)
  const budgetCurrencyDropdownRef = React.useRef<HTMLDivElement>(null)

  const [quantity, setQuantity] = useState<number | "">("")
  const [quantityUnit, setQuantityUnit] = useState("")
  const [isUnitDropdownOpen, setIsUnitDropdownOpen] = useState(false)
  const unitDropdownRef = React.useRef<HTMLDivElement>(null)

  const [formError, setFormError] = useState<string | null>(null)
  const [formAction, setFormAction] = useState<"revert" | "forward" | null>(null)

  const quantityError = getQuantityError(quantity)
  const budgetError = getBudgetError(budget)
  const unitError = quantity !== "" && !quantityUnit ? "Select quantity unit" : null
  const currencyError = budget.trim() !== "" && !budgetCurrency ? "Select currency code" : null

  useEffect(() => {
    if (!formError) return
    const scrollTimer = setTimeout(() => {
      const targetId = formAction === "revert" ? "admin-quotation-charges-section" : "forward-form-error"
      const el = document.getElementById(targetId)
      el?.scrollIntoView({ behavior: "smooth", block: "center" })
    }, 50)
    const dismissTimer = setTimeout(() => {
      setFormError(null)
    }, 5000)
    return () => {
      clearTimeout(scrollTimer)
      clearTimeout(dismissTimer)
    }
  }, [formError, formAction])

  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const actionMenuRef = React.useRef<HTMLDivElement>(null)
  const currencyDropdownRef = React.useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (budgetCurrencyDropdownRef.current && !budgetCurrencyDropdownRef.current.contains(event.target as Node)) {
        setIsBudgetCurrencyDropdownOpen(false)
      }
      if (unitDropdownRef.current && !unitDropdownRef.current.contains(event.target as Node)) {
        setIsUnitDropdownOpen(false)
      }
      if (actionMenuRef.current && !actionMenuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false)
      }
      if (currencyDropdownRef.current && !currencyDropdownRef.current.contains(event.target as Node)) {
        setIsCurrencyDropdownOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const [paymentTerms, setPaymentTerms] = useState("")
  const [leadTime, setLeadTime] = useState("")
  const [targetsList, setTargetsList] = useState<any[]>([])
  const [selectedCompany, setSelectedCompany] = useState<any | null>(null)
  const [selectedExternalSeller, setSelectedExternalSeller] = useState<any | null>(null)
  const [selectedProduct, setSelectedProduct] = useState<any | null>(null)
  const [selectedVariant, setSelectedVariant] = useState<any | null>(null)

  const [currencyCode, setCurrencyCode] = useState("INR")
  const [isCurrencyDropdownOpen, setIsCurrencyDropdownOpen] = useState(false)
  const [charges, setCharges] = useState<ChargeItem[]>([{ ...DEFAULT_CHARGE }])

  const canAddCharge = charges.every(isChargeRowCompleteAndValid)

  const handleAddCharge = () => {
    if (!canAddCharge) {
      toast.error("Please complete the validation of the current charge before adding another.")
      return
    }
    setCharges([...charges, { ...DEFAULT_CHARGE }])
  }

  const handleRemoveCharge = (index: number) => {
    setCharges(charges.filter((_, i) => i !== index))
  }

  const blockNegativeKeys = (e: React.KeyboardEvent<HTMLInputElement>, currentVal?: any, isIntegerOnly?: boolean) => {
    if (
      e.key === "-" ||
      e.key === "Subtract" ||
      e.code === "Minus" ||
      e.code === "NumpadSubtract" ||
      e.key === "+" ||
      e.key === "e" ||
      e.key === "E"
    ) {
      e.preventDefault()
    }
    if (isIntegerOnly && (e.key === "." || e.key === "Decimal")) {
      e.preventDefault()
    }
    if (e.key === "." && currentVal !== undefined && String(currentVal).includes(".")) {
      e.preventDefault()
    }
  }

  const handleChargeChange = (index: number, field: keyof ChargeItem, value: any) => {
    let sanitized = value
    if (typeof sanitized === "string") {
      if (field === "quantity") {
        sanitized = sanitized.replace(/\D/g, "")
      } else if (["unitPrice", "discount", "tax"].includes(field as string)) {
        sanitized = sanitized.replace(/[^0-9.]/g, "")
        const parts = sanitized.split(".")
        if (parts.length > 2) {
          sanitized = parts[0] + "." + parts.slice(1).join("")
        }
      }
    }
    const updated = [...charges]
    updated[index] = { ...updated[index], [field]: sanitized }
    setCharges(updated)
  }

  useEffect(() => {
    setEditedMessage(rfq.queryMessage || "")
    setStatus(rfq.status || "OPEN")
    setValidUntil(rfq.validUntil ? rfq.validUntil.split('T')[0] : "")
    setCanSellerAccess(rfq.canSellerAccess !== false)
    setCountry(rfq.country ? String(rfq.country) : "")
    setAddress(rfq.address ? String(rfq.address) : "")
    setMoq(rfq.moq ? String(rfq.moq) : "")
    setQuantity(rfq.quantity !== null && rfq.quantity !== undefined ? Number(rfq.quantity) : "")
    setQuantityUnit(rfq.measureUnit || rfq.quantityUnit || "")
    setBudget(rfq.budget ? String(rfq.budget) : "")
    setBudgetCurrency(rfq.currencyCode || "")
    setPaymentTerms(rfq.paymentTerms ? String(rfq.paymentTerms) : "")
    setLeadTime(rfq.leadTime ? String(rfq.leadTime) : rfq.estimatedTime ? String(rfq.estimatedTime) : "")
    setTargetsList((rfq.targets || []).map((t: any) => ({
      id: t.id,
      productId: t.productId,
      productVariantId: t.productVariantId,
      quantity: t.quantity || 1,
      productName: t.product?.productName || t.productId || "General Product Reference"
    })))
    setCurrencyCode("INR")
    setCharges([{ ...DEFAULT_CHARGE }])
    setFormAction(null)
    setShowAddCompanyInline(false)
    companyAbortRef.current?.abort()
    setSelectedCompanyId("")
    setCompanySearchQuery("")
    setCompanyDisplayName("")
    setCompanySearchResults([])
    setCompanySearchEmpty(false)
    setIsCompanySearchLoading(false)
    setIsCompanySearchDropdownOpen(false)
    modalCompanyAbortRef.current?.abort()
    setModalSelectedCompanyId("")
    setModalCompanySearchQuery("")
    setModalCompanyDisplayName("")
    setModalCompanySearchResults([])
    setModalCompanySearchEmpty(false)
    setIsModalCompanySearchLoading(false)
    setIsModalCompanySearchDropdownOpen(false)
  }, [rfq])

  const CURRENCIES = [
  { code: "INR", name: "INR - Indian Rupee" },
  { code: "USD", name: "USD - US Dollar" },
  { code: "EUR", name: "EUR - Euro" },
  { code: "GBP", name: "GBP - British Pound" },
  { code: "AED", name: "AED - UAE Dirham" },
  { code: "SGD", name: "SGD - Singapore Dollar" },
]
  
  const QUANTITY_UNITS = [
    { code: "kg", name: "Kilogram (kg)" },
    { code: "g", name: "Gram (g)" },
    { code: "ltr", name: "Liter (ltr)" },
    { code: "ml", name: "Milliliter (ml)" },
    { code: "pcs", name: "Pieces (pcs)" },
    { code: "qtl", name: "Quintal (qtl)" },
    { code: "ton", name: "Metric Ton (ton)" },
    { code: "box", name: "Box (box)" },
  ]
  
  const forwardMutation = useForwardRfq()
  const sendQuotationMutation = useSendQuotationToBuyer()
  const addCompanyMutation = useAddCompanyToRfq()
  const isSubmitting = forwardMutation.isPending || sendQuotationMutation.isPending || addCompanyMutation.isPending

  const [showAddCompanyInline, setShowAddCompanyInline] = useState(false)
  const [modalSelectedCompanyId, setModalSelectedCompanyId] = useState("")
  const [modalCompanySearchQuery, setModalCompanySearchQuery] = useState("")
  const [modalCompanyDisplayName, setModalCompanyDisplayName] = useState("")
  const [modalCompanySearchResults, setModalCompanySearchResults] = useState<any[]>([])
  const [isModalCompanySearchDropdownOpen, setIsModalCompanySearchDropdownOpen] = useState(false)
  const [modalCompanySearchEmpty, setModalCompanySearchEmpty] = useState(false)
  const [isModalCompanySearchLoading, setIsModalCompanySearchLoading] = useState(false)
  const modalCompanyAbortRef = useRef<AbortController | null>(null)
  const isModalCompanySelectionRef = useRef(false)

  const isOpenRfq = rfq.type?.toUpperCase() === "OPEN"

  // Way 1 (OPEN RFQ): Live debounced company search states
  const [companySearchQuery, setCompanySearchQuery] = useState("")
  const [companyDisplayName, setCompanyDisplayName] = useState("")
  const [companySearchResults, setCompanySearchResults] = useState<any[]>([])
  const [isCompanySearchDropdownOpen, setIsCompanySearchDropdownOpen] = useState(false)
  const [companySearchEmpty, setCompanySearchEmpty] = useState(false)
  const [isCompanySearchLoading, setIsCompanySearchLoading] = useState(false)
  const companyAbortRef = useRef<AbortController | null>(null)
  const isCompanySelectionRef = useRef(false)

  // Way 2 (CLOSE RFQ): Fetch pre-linked companies for this RFQ
  const { data: rfqCompanies, isLoading: loadingRfqCompanies } = useGetCompaniesByRfqId(
    rfq.id,
    activeTab === "buyers" && isOpen && !isOpenRfq
  )
  const companiesList = (rfqCompanies || []).filter((c: any) => c && c.id)
  const loadingCompanies = loadingRfqCompanies

  const [selectedCompanyId, setSelectedCompanyId] = useState("")

  // Auto-select first valid company ONLY for Way 2 (CLOSE RFQ with pre-linked companies)
  useEffect(() => {
    if (isOpenRfq) return

    if (companiesList && companiesList.length > 0) {
      const exists = companiesList.some((c: any) => c.id === selectedCompanyId)
      if (!selectedCompanyId || !exists) {
        const firstValid = companiesList.find((c: any) => c && c.id)
        setSelectedCompanyId(firstValid ? firstValid.id : "")
      }
    } else {
      setSelectedCompanyId("")
    }
  }, [isOpenRfq, companiesList, selectedCompanyId])

  // Debounced 800ms company search for Way 1 (OPEN RFQ)
  useEffect(() => {
    if (!isOpenRfq) return

    if (isCompanySelectionRef.current) {
      isCompanySelectionRef.current = false
      return
    }

    const val = companySearchQuery.trim()
    if (companyDisplayName && val === companyDisplayName.trim()) {
      setIsCompanySearchLoading(false)
      setIsCompanySearchDropdownOpen(false)
      return
    }

    if (val.length < 2) {
      setCompanySearchResults([])
      setCompanySearchEmpty(false)
      setIsCompanySearchDropdownOpen(false)
      setIsCompanySearchLoading(false)
      companyAbortRef.current?.abort()
      return
    }

    setIsCompanySearchLoading(true)
    const timer = setTimeout(async () => {
      companyAbortRef.current?.abort()
      const controller = new AbortController()
      companyAbortRef.current = controller

      try {
        const res = await searchCompanies(val, token!, controller.signal)
        if (!controller.signal.aborted) {
          const results = res || []
          setCompanySearchResults(results)
          setCompanySearchEmpty(results.length === 0)
          setIsCompanySearchDropdownOpen(true)
        }
      } catch (err: any) {
        if (err?.name !== "CanceledError" && err?.code !== "ERR_CANCELED") {
          console.error(err)
        }
      } finally {
        if (!controller.signal.aborted) setIsCompanySearchLoading(false)
      }
    }, 800)

    return () => {
      clearTimeout(timer)
    }
  }, [isOpenRfq, companySearchQuery, companyDisplayName, token])

  // Cleanup abort controller on unmount
  useEffect(() => {
    return () => {
      companyAbortRef.current?.abort()
    }
  }, [])

  // Debounced 800ms company search for Add Company inline modal
  useEffect(() => {
    if (!showAddCompanyInline) return

    if (isModalCompanySelectionRef.current) {
      isModalCompanySelectionRef.current = false
      return
    }

    const val = modalCompanySearchQuery.trim()
    if (modalCompanyDisplayName && val === modalCompanyDisplayName.trim()) {
      setIsModalCompanySearchLoading(false)
      setIsModalCompanySearchDropdownOpen(false)
      return
    }

    if (val.length < 2) {
      setModalCompanySearchResults([])
      setModalCompanySearchEmpty(false)
      setIsModalCompanySearchDropdownOpen(false)
      setIsModalCompanySearchLoading(false)
      modalCompanyAbortRef.current?.abort()
      return
    }

    setIsModalCompanySearchLoading(true)
    const timer = setTimeout(async () => {
      modalCompanyAbortRef.current?.abort()
      const controller = new AbortController()
      modalCompanyAbortRef.current = controller

      try {
        const res = await searchCompanies(val, token!, controller.signal)
        if (!controller.signal.aborted) {
          const results = res || []
          setModalCompanySearchResults(results)
          setModalCompanySearchEmpty(results.length === 0)
          setIsModalCompanySearchDropdownOpen(true)
        }
      } catch (err: any) {
        if (err?.name !== "CanceledError" && err?.code !== "ERR_CANCELED") {
          console.error(err)
        }
      } finally {
        if (!controller.signal.aborted) setIsModalCompanySearchLoading(false)
      }
    }, 800)

    return () => {
      clearTimeout(timer)
    }
  }, [showAddCompanyInline, modalCompanySearchQuery, modalCompanyDisplayName, token])

  // Cleanup modal abort controller on unmount
  useEffect(() => {
    return () => {
      modalCompanyAbortRef.current?.abort()
    }
  }, [])

  const handleForward = (e: React.FormEvent) => {
    e.preventDefault()
    setFormError(null)

    const isRevertAction = rfq.type?.toUpperCase() === "OPEN" && formAction === "revert"

    const reportValidationError = (errMsg: string, scrollTargetId?: string) => {
      setFormError(errMsg)
      setTimeout(() => {
        if (scrollTargetId) {
          document.getElementById(scrollTargetId)?.scrollIntoView({ behavior: "smooth", block: "center" })
        } else {
          document.getElementById(isRevertAction ? "admin-quotation-charges-section" : "forward-form-error")?.scrollIntoView({ behavior: "smooth", block: "center" })
        }
      }, 50)
    }

    if (!selectedCompanyId && !isRevertAction) {
      reportValidationError(
        isOpenRfq ? "Please search and select a target company." : "Please select a target company.",
        "forward-target-company"
      )
      return
    }

    if (!editedMessage.trim()) {
      reportValidationError(
        isRevertAction ? "Message / Terms is required to revert quotation." : "Procurement Request is required.",
        isRevertAction ? "admin-quotation-charges-section" : "forward-procurement-request"
      )
      return
    }

    if (isRevertAction) {
      if (!currencyCode.trim()) {
        reportValidationError("Currency code is required.", "admin-quotation-charges-section")
        return
      }
      if (charges.length === 0) {
        reportValidationError("At least one charge is required.", "admin-quotation-charges-section")
        return
      }

      for (let i = 0; i < charges.length; i++) {
        const charge = charges[i]
        const descErr = getChargeDescriptionError(charge.description)
        const unitPriceErr = getChargeUnitPriceError(charge.unitPrice)
        const quantityErr = getChargeQuantityError(charge.quantity)
        const discountErr = getChargeDiscountError(charge.discount)
        const taxErr = getChargeTaxError(charge.tax)

        if (!charge.description.trim()) {
          reportValidationError(`Description for charge #${i + 1} is required.`, "admin-quotation-charges-section")
          return
        }
        if (descErr) {
          reportValidationError(`Charge #${i + 1} Description: ${descErr}`, "admin-quotation-charges-section")
          return
        }
        if (charge.unitPrice === "" || isNaN(Number(charge.unitPrice))) {
          reportValidationError(`Unit price for charge #${i + 1} is required.`, "admin-quotation-charges-section")
          return
        }
        if (unitPriceErr) {
          reportValidationError(`Charge #${i + 1} Unit Price: ${unitPriceErr}`, "admin-quotation-charges-section")
          return
        }
        if (charge.quantity === "" || isNaN(Number(charge.quantity))) {
          reportValidationError(`Quantity for charge #${i + 1} is required.`, "admin-quotation-charges-section")
          return
        }
        if (quantityErr) {
          reportValidationError(`Charge #${i + 1} Quantity: ${quantityErr}`, "admin-quotation-charges-section")
          return
        }
        if (discountErr) {
          reportValidationError(`Charge #${i + 1} Discount: ${discountErr}`, "admin-quotation-charges-section")
          return
        }
        if (taxErr) {
          reportValidationError(`Charge #${i + 1} Tax: ${taxErr}`, "admin-quotation-charges-section")
          return
        }
        if (!isChargeRowCompleteAndValid(charge)) {
          reportValidationError(`Please ensure all fields in charge #${i + 1} are complete and valid.`, "admin-quotation-charges-section")
          return
        }
      }

      sendQuotationMutation.mutate({
        rfqId: rfq.id,
        adminToSellerRfqId: "",
        companyId: selectedCompanyId || rfq.sellerCompanyId || rfq.companyId || "",
        message: editedMessage.trim(),
        currencyCode: currencyCode.trim(),
        expiresAt: validUntil ? (validUntil.includes("T") ? validUntil : `${validUntil}T12:00:00.000Z`) : undefined,
        charges: charges.map((c) => {
          const base = Number(c.unitPrice || 0) * Number(c.quantity || 0)
          const subtotal = base - (base * (Number(c.discount || 0) / 100))
          const amount = Math.max(0, subtotal + (subtotal * (Number(c.tax || 0) / 100)))
          return {
            description: c.description.trim(),
            amount,
            unitPrice: Number(c.unitPrice || 0),
            quantity: Number(c.quantity || 1),
            discount: Number(c.discount || 0),
            tax: Number(c.tax || 0),
            taxType: c.taxType
          }
        })
      }, {
        onSuccess: () => {
          onForwardSuccess("Quotation reverted to buyer successfully!")
          navigate({ to: "/quotations", search: { subTab: "buyer", page: 1 } as any })
        },
        onError: (err: any) => {
          setFormError(err?.message || "Failed to revert RFQ. Please try again.")
        }
      })
    } else {
      if (quantityError) {
        reportValidationError(quantityError, "forward-target-quantity")
        return
      }
      if (unitError) {
        reportValidationError(unitError, "forward-target-quantity")
        return
      }

      if (budgetError) {
        reportValidationError(budgetError, "forward-target-budget")
        return
      }
      if (currencyError) {
        reportValidationError(currencyError, "forward-target-budget")
        return
      }

      const formattedValidUntil = validUntil ? new Date(validUntil).toISOString() : null

      forwardMutation.mutate({
        buyerRfqId: rfq.id,
        companyId: selectedCompanyId,
        status,
        queryMessage: editedMessage.trim(),
        validUntil: formattedValidUntil,
        canSellerAccess,
        country: country.trim() || null,
        address: address.trim() || null,
        quantity: quantity !== "" ? Number(quantity) : null,
        measureUnit: quantityUnit || null,
        budget: budget.trim() ? Number(budget.trim()) : null,
        currencyCode: budgetCurrency || null,
        paymentTerms: paymentTerms.trim() || null,
        leadTime: leadTime.trim() || null,
        targets: targetsList.map((t: any) => ({
          productId: t.productId || null,
          productVariantId: t.productVariantId || null,
          quantity: Number(t.quantity) || 1
        }))
      }, {
        onSuccess: () => {
          onForwardSuccess("RFQ forwarded to seller successfully!")
          navigate({ to: "/rfqs", search: { subTab: "sellers" } as any })
        },
        onError: (err: any) => {
          setFormError(err?.message || "Failed to forward RFQ. Please try again.")
        }
      })
    }
  }

  const handleAddCompanySubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!modalSelectedCompanyId) return

    try {
      await addCompanyMutation.mutateAsync({
        rfqId: rfq.id,
        companyId: modalSelectedCompanyId
      })
      setShowAddCompanyInline(false)
      modalCompanyAbortRef.current?.abort()
      setModalSelectedCompanyId("")
      setModalCompanySearchQuery("")
      setModalCompanyDisplayName("")
      setModalCompanySearchResults([])
      setModalCompanySearchEmpty(false)
      setIsModalCompanySearchLoading(false)
      setIsModalCompanySearchDropdownOpen(false)
      onForwardSuccess("Company added to RFQ successfully!")
    } catch (err: any) {
      console.error(err)
      toast.error(err?.message || "Failed to add company")
    }
  }

  const hasProductOrVariantTargets = (rfq.targets || []).some((t: any) => t.product || t.productVariant)
  const targets = hasProductOrVariantTargets
    ? rfq.targets
    : rfq.company
    ? [{ id: rfq.company.id || "company-target", company: rfq.company, companyId: rfq.company.id, quantity: rfq.targets?.[0]?.quantity }]
    : (rfq.targets || [])

  const validUntilDate = rfq.validUntil ? new Date(rfq.validUntil) : null
  const diffTime = validUntilDate ? validUntilDate.getTime() - new Date().getTime() : 0
  const diffDays = validUntilDate ? Math.ceil(diffTime / (1000 * 60 * 60 * 24)) : 0
  const expirySubtext = validUntilDate 
    ? diffDays > 0 
      ? `Expiring in ${diffDays} day${diffDays !== 1 ? "s" : ""}` 
      : "Expired"
    : "No expiry specified"

  const isFormActive = activeTab === "buyers" && isOpen && (rfq.type?.toUpperCase() !== "OPEN" || formAction !== null)

  return (
    <div className={cn("animate-in fade-in duration-200 text-left font-sans max-w-5xl mx-auto", isFormActive && "pb-64 sm:pb-48 md:pb-16")}>
      <div className="group relative overflow-hidden bg-white border border-[#be001e26] hover:border-[#be001e4d] rounded-2xl p-5 space-y-4 shadow-sm hover:shadow-[0_20px_50px_rgba(230,32,47,0.06)] transition-all duration-300">
        <div className="absolute top-0 left-0 right-0 h-[1px] group-hover:h-[3.5px] bg-gradient-to-r from-[#be001e] to-[#e6202f] rounded-t-2xl opacity-0 group-hover:opacity-100 transition-all duration-300" />
        
        <div className="flex flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="flex items-center gap-2 rounded-lg border border-[#be001e]/20 bg-white hover:bg-[#be001e]/5 hover:text-[#be001e] hover:shadow-sm transition-all cursor-pointer p-1"
              title="Back to RFQ Queue"
            >
              <ChevronLeft className="size-4" />
            </button>
            <span className="text-sm text-slate-700 font-medium ">Back to RFQs</span>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <span className={`inline-flex items-center gap-0.5 rounded px-2.5 py-1 text-[10px] font-bold border uppercase tracking-wider ${
              resolvedStatus === "OPEN"
                ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                : (resolvedStatus === "PENDING" || resolvedStatus === "REQUEST_REVISION")
                ? "bg-amber-50 text-amber-700 border-amber-100"
                : (resolvedStatus === "EXPIRED" || resolvedStatus === "CANCELLED" || resolvedStatus === "REJECTED")
                ? "bg-red-50 text-red-700 border-red-100"
                : resolvedStatus === "QUOTED"
                ? "bg-blue-50 text-blue-700 border-blue-100"
                : resolvedStatus === "FORWARDED"
                ? "bg-indigo-50 text-indigo-700 border-indigo-100"
                : "bg-slate-50 text-slate-700 border-slate-200"
            }`}>
              {resolvedStatus === "REQUEST_REVISION" ? "REQUEST REVISION" : resolvedStatus}
            </span>
          </div>
        </div>

        <div className="h-px bg-[#be001e1a] w-full" />

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-3 space-y-4">
            {/* Buyer Name */}
            {(rfq.buyer?.user?.name || rfq.buyerToAdminRfq?.buyer?.user?.name || rfq.buyerToAdminRFQ?.buyer?.user?.name) && (
              <div className="space-y-1.5">
                <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1 font-sans">
                  <Building2 className="size-3.5 text-[#e6202f]" /> Buyer Name
                </h4>
                <div className="flex items-center justify-between gap-4 rounded-xl border border-[#be001e]/15 bg-slate-50/20 p-3 shadow-sm transition-all duration-200">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-xs text-slate-700 font-semibold tracking-tight font-sans">
                      {rfq.buyer?.user?.name || rfq.buyerToAdminRfq?.buyer?.user?.name || rfq.buyerToAdminRFQ?.buyer?.user?.name}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <div className="flex items-center justify-between mb-1 relative">
                <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                  <Package className="size-3.5 text-[#e6202f]" /> Sourcing Target
                </h4>
                <div ref={actionMenuRef} className="relative">
                  <button
                    type="button"
                    onClick={() => setIsMenuOpen((prev) => !prev)}
                    className="p-1 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-700 cursor-pointer transition-colors"
                  >
                    <MoreVertical className="size-4" />
                  </button>

                  {isMenuOpen && (
                    <div className="absolute right-0 top-7 z-10 w-33 rounded-xl border border-slate-200 bg-white py-1 shadow-lg">
                      <button
                        type="button"
                        onClick={() => {
                          setShowAddCompanyInline(true)
                          setIsMenuOpen(false)
                          modalCompanyAbortRef.current?.abort()
                          setModalSelectedCompanyId("")
                          setModalCompanySearchQuery("")
                          setModalCompanyDisplayName("")
                          setModalCompanySearchResults([])
                          setModalCompanySearchEmpty(false)
                          setIsModalCompanySearchLoading(false)
                          setIsModalCompanySearchDropdownOpen(false)
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-left text-[11px] font-bold tracking-wider text-slate-700 hover:bg-slate-50 hover:text-[#be001e] transition-colors cursor-pointer"
                      >
                        <Plus className="size-3.5" />Add Company
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {showAddCompanyInline && (
                <div className="bg-[#be001e05] border border-[#be001e] rounded-xl p-4.5 mb-3 space-y-4 shadow-[0_8px_30px_rgba(190,0,30,0.06)] transition-all duration-200">
                  <form onSubmit={handleAddCompanySubmit} className="space-y-4">
                    <div className="space-y-1 text-left">
                      <label className="text-[9.5px] font-extrabold uppercase tracking-wider text-slate-400 block">
                        Select Target Company
                      </label>
                      <div className="relative font-sans w-full">
                        <div className={cn(
                          "flex items-center gap-2 h-9 bg-white border px-3 rounded-lg transition-all",
                          addCompanyMutation.isError && !modalSelectedCompanyId
                            ? "border-[#be001e] focus-within:border-[#be001e] ring-1 ring-[#be001e]/10"
                            : "border-slate-200 focus-within:border-[#be001e]"
                        )}>
                          <input
                            type="text"
                            placeholder="Search company by name..."
                            value={modalCompanySearchQuery}
                            onChange={(e) => {
                              setModalCompanySearchQuery(e.target.value)
                              if (modalSelectedCompanyId && e.target.value !== modalCompanyDisplayName) {
                                setModalSelectedCompanyId("")
                              }
                            }}
                            disabled={addCompanyMutation.isPending}
                            className="w-full text-xs font-semibold text-slate-700 border-none focus:ring-0 p-0 outline-none bg-transparent placeholder-slate-400"
                          />
                          {(modalSelectedCompanyId || modalCompanySearchQuery) && (
                            <button
                              type="button"
                              onClick={() => {
                                modalCompanyAbortRef.current?.abort()
                                setModalSelectedCompanyId("")
                                setModalCompanySearchQuery("")
                                setModalCompanyDisplayName("")
                                setModalCompanySearchResults([])
                                setModalCompanySearchEmpty(false)
                                setIsModalCompanySearchLoading(false)
                                setIsModalCompanySearchDropdownOpen(false)
                              }}
                              disabled={addCompanyMutation.isPending}
                              className="text-[#be001e] hover:text-[#be001e]/80 cursor-pointer p-0.5"
                            >
                              {isModalCompanySearchLoading ? (
                                <Loader2 className="size-3.5 animate-spin text-[#be001e]" />
                              ) : (
                                <X className="size-3.5 text-[#be001e]" />
                              )}
                            </button>
                          )}
                        </div>

                        {isModalCompanySearchDropdownOpen && modalCompanySearchResults.length > 0 && (
                          <>
                            <div
                              className="fixed inset-0 z-30"
                              onClick={() => setIsModalCompanySearchDropdownOpen(false)}
                            />
                            <div className="absolute left-0 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg py-1.5 z-40 max-h-[180px] overflow-y-auto scrollbar-thin">
                              {modalCompanySearchResults.map((comp: any) => (
                                <button
                                  key={comp.id}
                                  type="button"
                                  onClick={() => {
                                    const selectedName = comp.name || comp.legalName || comp.id
                                    isModalCompanySelectionRef.current = true
                                    setModalSelectedCompanyId(comp.id)
                                    setModalCompanyDisplayName(selectedName)
                                    setModalCompanySearchQuery(selectedName)
                                    setModalCompanySearchResults([])
                                    setModalCompanySearchEmpty(false)
                                    setIsModalCompanySearchLoading(false)
                                    setIsModalCompanySearchDropdownOpen(false)
                                  }}
                                  className={cn(
                                    "w-full text-left px-3 py-1.5 text-xs transition-colors hover:bg-[#be001e]/5 hover:text-[#be001e] cursor-pointer block font-semibold",
                                    modalSelectedCompanyId === comp.id ? "text-[#be001e] bg-[#be001e]/5" : "text-slate-700"
                                  )}
                                >
                                  <div className="flex items-center justify-between">
                                    <span>{comp.name || comp.legalName}</span>
                                    {comp.city && (
                                      <span className="text-[10px] text-slate-400 font-normal">
                                        {comp.city}{comp.country ? `, ${comp.country}` : ""}
                                      </span>
                                    )}
                                  </div>
                                </button>
                              ))}
                            </div>
                          </>
                        )}

                        {isModalCompanySearchDropdownOpen && modalCompanySearchEmpty && (
                          <div className="absolute left-0 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg py-2 z-40 flex flex-col items-center gap-1.5">
                            <p className="text-[11px] font-bold text-slate-500">No company found</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {addCompanyMutation.isError && (
                      <div className="flex items-center gap-1.5 text-xs font-semibold text-[#e6202f] bg-red-50 p-2.5 rounded-lg border border-red-100">
                        <AlertCircle className="size-4 shrink-0" />
                        <span>{(addCompanyMutation.error as Error)?.message || "Failed to add company"}</span>
                      </div>
                    )}

                    <div className="flex gap-2.5 justify-end pt-1">
                      <button
                        type="button"
                        onClick={() => {
                          setShowAddCompanyInline(false)
                          modalCompanyAbortRef.current?.abort()
                          setModalSelectedCompanyId("")
                          setModalCompanySearchQuery("")
                          setModalCompanyDisplayName("")
                          setModalCompanySearchResults([])
                          setModalCompanySearchEmpty(false)
                          setIsModalCompanySearchLoading(false)
                          setIsModalCompanySearchDropdownOpen(false)
                        }}
                        disabled={addCompanyMutation.isPending}
                        className="px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-lg cursor-pointer border border-slate-200 transition-colors disabled:opacity-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={addCompanyMutation.isPending || !modalSelectedCompanyId}
                        className="px-3 py-1.5 bg-[#be001e] hover:bg-[#e6202f] text-white text-xs font-bold rounded-lg cursor-pointer flex items-center gap-1.5 transition-colors disabled:opacity-50"
                      >
                        {addCompanyMutation.isPending ? (
                          <>
                            <Loader2 className="size-3 animate-spin" />
                            Adding...
                          </>
                        ) : (
                          <>Add Company</>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {targets && targets.length > 0 ? (
                <div className="grid grid-cols-1 gap-2">
                  {targets.map((target: any) => (
                    <TargetItemView
                      key={target.id}
                      target={target}
                      onCompanyClick={(c) => setSelectedCompany(c)}
                      onProductClick={(p) => setSelectedProduct(p)}
                      onVariantClick={(v) => setSelectedVariant(v)}
                      onExternalSellerClick={setSelectedExternalSeller}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-xs text-slate-400 font-medium bg-slate-50/50 border border-dashed border-slate-200 rounded-xl">
                  No sourcing targets linked to this RFQ yet.
                </div>
              )}
            </div>
            {displayMessage && displayMessage.trim() && (
              <div className="space-y-1.5">
                <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                  <FileText className="size-3.5 text-[#e6202f]" /> Procurement Request
                </h4>
                <p className="text-sm text-slate-700 font-medium bg-[#be001e03] p-3.5 rounded-xl border border-[#be001e26] hover:border-[#be001e40] transition-colors leading-relaxed italic">
                  {displayMessage}
                </p>
              </div>
            )}


            {(rfq.country || rfq.estimatedTime || rfq.moq || rfq.budget || rfq.paymentTerms || rfq.address) && (
              <div className="space-y-2">
                <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                  <Tag className="size-3.5 text-[#e6202f]" /> Sourcing Specifications
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-slate-50/50 border border-slate-200/50 p-4 rounded-xl">


                  {/* Quantity */}
                  {rfq.quantity && (
                    <div className="flex items-center gap-3 p-2.5 bg-white rounded-lg border border-slate-100 shadow-sm hover:border-[#be001e]/10 transition-colors">
                      <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-[#be001e]/5 text-[#e6202f]">
                        <Hash className="size-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Quantity</p>
                        <p className="text-xs font-bold text-slate-750">{rfq.quantity + " " + rfq.measureUnit}</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Budget */}
                  {rfq.budget && (
                    <div className="flex items-center gap-3 p-2.5 bg-white rounded-lg border border-slate-100 shadow-sm hover:border-[#be001e]/10 transition-colors">
                      <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-[#be001e]/5 text-[#e6202f]">
                        <Tag className="size-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Target Budget</p>
                        <p className="text-xs font-bold text-slate-750">{rfq.currencyCode + " " + rfq.budget}</p>
                      </div>
                    </div>
                  )}

                  {/* Payment Terms */}
                  {rfq.paymentTerms && (
                    <div className="flex items-center gap-3 p-2.5 bg-white rounded-lg border border-slate-100 shadow-sm hover:border-[#be001e]/10 transition-colors">
                      <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-[#be001e]/5 text-[#e6202f]">
                        <FileText className="size-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Payment Terms</p>
                        <p className="text-xs font-bold text-slate-750">{rfq.paymentTerms}</p>
                      </div>
                    </div>
                  )}

                  {/* Estimated Lead Time */}
                  {rfq.leadTime && (
                    <div className="flex items-center gap-3 p-2.5 bg-white rounded-lg border border-slate-100 shadow-sm hover:border-[#be001e]/10 transition-colors">
                      <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-[#be001e]/5 text-[#e6202f]">
                        <Calendar className="size-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Lead Time</p>
                        <p className="text-xs font-bold text-slate-750">{formatDate(rfq.leadTime)}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Logistics Specifications */}
            {(rfq.preferredShipmentMode || rfq.shipmentType) && (
              <div className="space-y-2">
                <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                  <Truck className="size-3.5 text-[#e6202f]" /> Logistics Specifications
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-slate-50/50 border border-slate-200/50 p-4 rounded-xl">
                  {/* Preferred Shipment Mode */}
                  {rfq.preferredShipmentMode && (
                    <div className="flex items-center gap-3 p-2.5 bg-white rounded-lg border border-slate-100 shadow-sm hover:border-[#be001e]/10 transition-colors">
                      <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-[#be001e]/5 text-[#e6202f]">
                        <Truck className="size-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Shipment Mode</p>
                        <p className="text-xs font-bold text-slate-750">{rfq.preferredShipmentMode}</p>
                      </div>
                    </div>
                  )}

                  {/* Shipment Type */}
                  {rfq.shipmentType && (
                    <div className="flex items-center gap-3 p-2.5 bg-white rounded-lg border border-slate-100 shadow-sm hover:border-[#be001e]/10 transition-colors">
                      <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-[#be001e]/5 text-[#e6202f]">
                        <Package className="size-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Shipment Type</p>
                        <p className="text-xs font-bold text-slate-750">{rfq.shipmentType.replace(/_/g, " ")}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === "buyers" && isOpen && (
              <div className="space-y-3">
                {rfq.type?.toUpperCase() === "OPEN" && formAction === null ? (
                  <div className="space-y-3 text-end">
                      {/* <button
                        type="button"
                        onClick={() => setFormAction("revert")}
                        className="px-4 py-2 bg-[#be001e] hover:bg-[#e6202f] text-white text-xs font-bold rounded-lg cursor-pointer transition-colors shadow-sm"
                      >
                        Revert RFQ
                      </button> */}
                      <button
                        type="button"
                        onClick={() => setFormAction("forward")}
                        className="px-4 py-2 bg-[#be001e] hover:bg-[#e6202f] text-white text-xs font-bold rounded-lg cursor-pointer transition-colors shadow-sm"
                      >
                        Forward RFQ
                      </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center gap-1">
                      <h4 className="text-[10px] font-black uppercase tracking-wider text-[#be001e]">
                        {formAction === "revert" ? "Revert RFQ to Buyer" : "Forward RFQ to Seller"}
                      </h4>
                    </div>

                    <div className="bg-[#be001e05] border border-[#be001e] rounded-xl p-4.5 space-y-4 shadow-[0_8px_30px_rgba(190,0,30,0.06)] transition-all duration-200">
                      <form onSubmit={handleForward} className="space-y-4">
                        {formError && formAction !== "revert" && (
                          <div id="forward-form-error" className="flex items-center gap-1.5 text-xs font-semibold text-[#e6202f] bg-red-50 p-2.5 rounded-lg border border-red-100 animate-in fade-in duration-200">
                            <AlertCircle className="size-4 shrink-0" />
                            <span>{formError}</span>
                          </div>
                        )}
                        
                        {formAction === "revert" ? (
                          <div className="space-y-4">
                          <div className="space-y-1" id="revert-message-terms">
                            <label className="text-[9px] font-bold uppercase tracking-wider text-slate-400">
                              Message / Terms
                            </label>
                            <textarea
                              value={editedMessage}
                              onChange={(e) => setEditedMessage(e.target.value)}
                              placeholder="Enter message to include in quotation..."
                              className={cn(
                                "w-full min-h-[70px] max-h-[140px] rounded-lg border bg-white p-2.5 text-xs text-slate-800 placeholder-slate-400 focus:outline-none transition-all duration-200",
                                !editedMessage.trim() && formError && formAction === "revert"
                                  ? "border-[#be001e] focus:border-[#be001e] ring-1 ring-[#be001e]/10"
                                  : "border-slate-200 focus:border-[#be001e]"
                              )}
                              disabled={isSubmitting}
                              required
                            />
                            {!editedMessage.trim() && formError && formAction === "revert" && (
                              <p className="text-[10px] text-[#be001e] font-semibold mt-1">
                                Message / Terms is required.
                              </p>
                            )}
                          </div>

                          <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8">
                            <div className="space-y-1 sm:space-y-0 sm:flex sm:items-center sm:gap-3">
                              <label className="text-[9px] font-bold uppercase tracking-wider text-slate-400 shrink-0 block">
                                Currency Code
                              </label>
                              <div className="relative w-full sm:w-[80px]" ref={currencyDropdownRef}>
                                <button
                                  type="button"
                                  onClick={() => setIsCurrencyDropdownOpen(!isCurrencyDropdownOpen)}
                                  disabled={isSubmitting}
                                  className={cn(
                                    "w-full flex items-center justify-between rounded-lg border bg-white px-2.5 py-2 text-xs text-slate-800 focus:outline-none transition-all duration-200 cursor-pointer text-left",
                                    !currencyCode.trim() && formError && formAction === "revert"
                                      ? "border-[#be001e] focus:border-[#be001e] ring-1 ring-[#be001e]/10"
                                      : "border-slate-200 focus:border-[#be001e]"
                                  )}
                                >
                                  <span>{currencyCode}</span>
                                  <ChevronDown className={`size-3.5 text-slate-400 transition-transform ${isCurrencyDropdownOpen ? "rotate-180" : ""}`} />
                                </button>

                                {isCurrencyDropdownOpen && (
                                  <ul className="absolute z-50 w-full mt-1 max-h-[136px] overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg overscroll-contain">
                                    {[
                                      "INR", "USD", "EUR", "GBP", "JPY", "CNY", "AED", "SAR",
                                      "CAD", "AUD", "SGD", "HKD", "CHF", "NZD", "ZAR", "RUB",
                                      "TRY", "KRW", "THB", "MYR", "IDR", "PHP", "VND", "BDT",
                                      "PKR", "LKR", "NPR", "BRL", "MXN", "EGP", "NGN", "KES",
                                      "ILS", "QAR", "KWD", "OMR", "BHD", "DKK", "SEK", "NOK",
                                      "PLN", "CZK", "HUF"
                                    ].map((code) => (
                                      <li
                                        key={code}
                                        onClick={() => {
                                          setCurrencyCode(code)
                                          setIsCurrencyDropdownOpen(false)
                                        }}
                                        className="px-2.5 py-2 text-xs font-medium text-slate-700 hover:bg-[#be001e]/5 hover:text-[#be001e] cursor-pointer transition-colors"
                                      >
                                        {code}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            </div>

                            <div className="space-y-1 sm:space-y-0 sm:flex sm:items-center sm:gap-3">
                              <label className="text-[9px] font-bold uppercase tracking-wider text-slate-400 shrink-0 block">
                                Valid Until
                              </label>
                              <div className="w-full sm:w-[170px]">
                                <DatePicker
                                  value={validUntil}
                                  onChange={setValidUntil}
                                  min={new Date().toISOString().split("T")[0]}
                                  placeholder="Select valid date"
                                  disabled={isSubmitting}
                                  className="!h-[34px] !py-1.5 !text-xs !bg-white !rounded-lg"
                                />
                              </div>
                            </div>
                          </div>

                          <div id="admin-quotation-charges-section" className="space-y-2">
                            <div className="flex items-center justify-between">
                              <label className="text-[9px] font-bold uppercase tracking-wider text-slate-500">
                                Charges / Fees
                              </label>
                              <button
                                type="button"
                                onClick={handleAddCharge}
                                disabled={isSubmitting}
                                className="flex items-center gap-1 text-[10px] font-bold text-[#be001e] hover:text-[#e6202f] transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <Plus className="size-3" /> Add Charge
                              </button>
                            </div>

                            {formError && (
                              <div className="flex items-center gap-1.5 text-xs font-semibold text-[#e6202f] bg-red-50 p-2.5 rounded-lg border border-red-100 animate-in fade-in duration-200">
                                <AlertCircle className="size-4 shrink-0" />
                                <span>{formError}</span>
                              </div>
                            )}

                            <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm mt-2">
                              <table className="w-full text-left text-xs whitespace-nowrap">
                                <thead className="bg-slate-50 text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                                  <tr>
                                    <th className="p-3">Description</th>
                                    <th className="p-3 w-32">Unit Price</th>
                                    <th className="p-3 w-24">Quantity</th>
                                    <th className="p-3 w-28">Discount (%)</th>
                                    <th className="p-3 w-24">Tax (%)</th>
                                    <th className="p-3 w-24">Tax Type</th>
                                    <th className="p-3 text-right">Total</th>
                                    <th className="p-3 w-10"></th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200 bg-white">
                                  {charges.map((charge, index) => {
                                    const base = (Number(charge.unitPrice) || 0) * (Number(charge.quantity) || 0)
                                    const subtotal = base - (base * ((Number(charge.discount) || 0) / 100))
                                    const total = subtotal + (subtotal * ((Number(charge.tax) || 0) / 100))

                                    const descError = getChargeDescriptionError(charge.description) || (!charge.description.trim() && formError && formAction === "revert" ? "Description is required" : null)
                                    const unitPriceError = getChargeUnitPriceError(charge.unitPrice) || (charge.unitPrice === "" && formError && formAction === "revert" ? "Unit price is required" : null)
                                    const quantityError = getChargeQuantityError(charge.quantity) || (charge.quantity === "" && formError && formAction === "revert" ? "Quantity is required" : null)
                                    const discountError = getChargeDiscountError(charge.discount)
                                    const taxError = getChargeTaxError(charge.tax)

                                    const isRowDisabled = index > 0 && !charges.slice(0, index).every(isChargeRowCompleteAndValid)

                                    return (
                                      <tr key={index} className={cn("hover:bg-slate-50/50 transition-colors align-top", isRowDisabled && "bg-slate-50/40 opacity-70")}>
                                        <td className="p-2 min-w-[200px]">
                                          <input
                                            type="text"
                                            value={charge.description}
                                            onChange={(e) => handleChargeChange(index, "description", e.target.value)}
                                            placeholder="e.g. Shipping Fee, Customs"
                                            className={cn(
                                              "w-full rounded-lg border bg-white px-2.5 py-2 text-xs text-slate-800 placeholder-slate-400 focus:outline-none transition-all duration-200",
                                              descError
                                                ? "border-[#be001e] focus:border-[#be001e] ring-1 ring-[#be001e]/10"
                                                : "border-slate-200 focus:border-[#be001e]",
                                              isRowDisabled && "disabled:opacity-50 disabled:cursor-not-allowed bg-slate-50"
                                            )}
                                            disabled={isSubmitting || isRowDisabled}
                                            title={isRowDisabled ? "Complete previous row validation first" : undefined}
                                            required
                                          />
                                          {descError && !isRowDisabled && (
                                            <p className="text-[10px] text-[#be001e] font-semibold mt-1">
                                              {descError}
                                            </p>
                                          )}
                                        </td>
                                        <td className="p-2 w-32">
                                          <input
                                            type="text"
                                            inputMode="decimal"
                                            value={charge.unitPrice}
                                            onChange={(e) => handleChargeChange(index, "unitPrice", e.target.value)}
                                            onKeyDown={(e) => blockNegativeKeys(e, charge.unitPrice)}
                                            placeholder="0.00"
                                            className={cn(
                                              "w-full text-center rounded-lg border bg-white px-2.5 py-2 text-xs text-slate-800 focus:outline-none transition-all duration-200 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                                              unitPriceError
                                                ? "border-[#be001e] focus:border-[#be001e] ring-1 ring-[#be001e]/10"
                                                : "border-slate-200 focus:border-[#be001e]",
                                              isRowDisabled && "disabled:opacity-50 disabled:cursor-not-allowed bg-slate-50"
                                            )}
                                            disabled={isSubmitting || isRowDisabled}
                                            title={isRowDisabled ? "Complete previous row validation first" : undefined}
                                            required
                                          />
                                          {unitPriceError && !isRowDisabled && (
                                            <p className="text-[10px] text-[#be001e] font-semibold mt-1 text-center whitespace-normal">
                                              {unitPriceError}
                                            </p>
                                          )}
                                        </td>
                                        <td className="p-2 w-24">
                                          <input
                                            type="text"
                                            inputMode="numeric"
                                            value={charge.quantity}
                                            onChange={(e) => handleChargeChange(index, "quantity", e.target.value)}
                                            onKeyDown={(e) => blockNegativeKeys(e, charge.quantity, true)}
                                            placeholder="1"
                                            className={cn(
                                              "w-full text-center rounded-lg border bg-white px-2.5 py-2 text-xs text-slate-800 focus:outline-none transition-all duration-200 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                                              quantityError
                                                ? "border-[#be001e] focus:border-[#be001e] ring-1 ring-[#be001e]/10"
                                                : "border-slate-200 focus:border-[#be001e]",
                                              isRowDisabled && "disabled:opacity-50 disabled:cursor-not-allowed bg-slate-50"
                                            )}
                                            disabled={isSubmitting || isRowDisabled}
                                            title={isRowDisabled ? "Complete previous row validation first" : undefined}
                                            required
                                          />
                                          {quantityError && !isRowDisabled && (
                                            <p className="text-[10px] text-[#be001e] font-semibold mt-1 text-center whitespace-normal">
                                              {quantityError}
                                            </p>
                                          )}
                                        </td>
                                        <td className="p-2 w-28">
                                          <input
                                            type="text"
                                            inputMode="decimal"
                                            value={charge.discount}
                                            onChange={(e) => handleChargeChange(index, "discount", e.target.value)}
                                            onKeyDown={(e) => blockNegativeKeys(e, charge.discount)}
                                            placeholder="0"
                                            className={cn(
                                              "w-full text-center rounded-lg border bg-white px-2.5 py-2 text-xs text-slate-800 focus:outline-none transition-all duration-200 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                                              discountError
                                                ? "border-[#be001e] focus:border-[#be001e] ring-1 ring-[#be001e]/10"
                                                : "border-slate-200 focus:border-[#be001e]",
                                              isRowDisabled && "disabled:opacity-50 disabled:cursor-not-allowed bg-slate-50"
                                            )}
                                            disabled={isSubmitting || isRowDisabled}
                                            title={isRowDisabled ? "Complete previous row validation first" : undefined}
                                          />
                                          {discountError && !isRowDisabled && (
                                            <p className="text-[10px] text-[#be001e] font-semibold mt-1 text-center whitespace-normal">
                                              {discountError}
                                            </p>
                                          )}
                                        </td>
                                        <td className="p-2 w-24">
                                          <input
                                            type="text"
                                            inputMode="decimal"
                                            value={charge.tax}
                                            onChange={(e) => handleChargeChange(index, "tax", e.target.value)}
                                            onKeyDown={(e) => blockNegativeKeys(e, charge.tax)}
                                            placeholder="18"
                                            className={cn(
                                              "w-full text-center rounded-lg border bg-white px-2.5 py-2 text-xs text-slate-800 focus:outline-none transition-all duration-200 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                                              taxError
                                                ? "border-[#be001e] focus:border-[#be001e] ring-1 ring-[#be001e]/10"
                                                : "border-slate-200 focus:border-[#be001e]",
                                              isRowDisabled && "disabled:opacity-50 disabled:cursor-not-allowed bg-slate-50"
                                            )}
                                            disabled={isSubmitting || isRowDisabled}
                                            title={isRowDisabled ? "Complete previous row validation first" : undefined}
                                          />
                                          {taxError && !isRowDisabled && (
                                            <p className="text-[10px] text-[#be001e] font-semibold mt-1 text-center whitespace-normal">
                                              {taxError}
                                            </p>
                                          )}
                                        </td>
                                        <td className="p-2 w-24">
                                          <select
                                            value={charge.taxType}
                                            onChange={(e) => handleChargeChange(index, "taxType", e.target.value)}
                                            className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-[11px] text-slate-800 focus:border-[#be001e] focus:outline-none transition-all duration-200 h-[34px] disabled:opacity-50 disabled:cursor-not-allowed"
                                            disabled={isSubmitting || isRowDisabled}
                                            title={isRowDisabled ? "Complete previous row validation first" : undefined}
                                          >
                                            <option value="GST">GST</option>
                                            <option value="VAT">VAT</option>
                                            <option value="NONE">None</option>
                                          </select>
                                        </td>
                                        <td className="p-2 text-center font-black text-slate-800 align-middle">
                                          <div className="flex items-center justify-center min-h-[34px]">
                                            {Math.max(0, total).toFixed(2)}
                                          </div>
                                        </td>
                                        <td className="p-2 text-center align-middle">
                                          <div className="flex items-center justify-center min-h-[34px]">
                                            {charges.length > 1 && (
                                              <button
                                                type="button"
                                                onClick={() => handleRemoveCharge(index)}
                                                disabled={isSubmitting}
                                                className="p-1.5 rounded-md border border-red-100 bg-red-50 hover:bg-red-100 text-[#e6202f] transition-all cursor-pointer shadow-sm"
                                                title="Remove charge"
                                              >
                                                <X className="size-3.5" />
                                              </button>
                                            )}
                                          </div>
                                        </td>
                                      </tr>
                                    )
                                  })}
                                </tbody>
                              </table>
                            </div>

                            <div className="flex justify-end p-3 bg-white border border-slate-200 rounded-xl mt-3 shadow-sm">
                              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mr-3 self-center">Grand Total:</span>
                              <span className="text-[12px] font-bold text-[#be001e]">
                                {currencyCode} {charges.reduce((acc, charge) => {
                                  const base = (Number(charge.unitPrice) || 0) * (Number(charge.quantity) || 0)
                                  const subtotal = base - (base * ((Number(charge.discount) || 0) / 100))
                                  return acc + Math.max(0, subtotal + (subtotal * ((Number(charge.tax) || 0) / 100)));
                                }, 0).toFixed(2)}
                              </span>
                            </div>
                          </div>
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-1 text-left md:col-span-2" id="forward-target-company">
                              <label className="text-[9.5px] font-extrabold uppercase tracking-wider text-slate-400 block">
                                Select Target Company
                              </label>
                              {isOpenRfq ? (
                                /* Way 1: Search Company Dropdown (OPEN RFQ) */
                                <div className="relative font-sans w-full">
                                  <div className={cn(
                                    "flex items-center gap-2 h-9 bg-white border px-3 rounded-lg transition-all",
                                    !selectedCompanyId && formError
                                      ? "border-[#be001e] focus-within:border-[#be001e] ring-1 ring-[#be001e]/10"
                                      : "border-slate-200 focus-within:border-[#be001e]"
                                  )}>
                                    <input
                                      type="text"
                                      placeholder="Search company by name..."
                                      value={companySearchQuery}
                                      onChange={(e) => {
                                        setCompanySearchQuery(e.target.value)
                                        if (selectedCompanyId && e.target.value !== companyDisplayName) {
                                          setSelectedCompanyId("")
                                        }
                                      }}
                                      disabled={isSubmitting}
                                      className="w-full text-xs font-semibold text-slate-700 border-none focus:ring-0 p-0 outline-none bg-transparent placeholder-slate-400"
                                    />
                                    {(selectedCompanyId || companySearchQuery) && (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          companyAbortRef.current?.abort()
                                          setSelectedCompanyId("")
                                          setCompanySearchQuery("")
                                          setCompanyDisplayName("")
                                          setCompanySearchResults([])
                                          setCompanySearchEmpty(false)
                                          setIsCompanySearchLoading(false)
                                          setIsCompanySearchDropdownOpen(false)
                                        }}
                                        disabled={isSubmitting}
                                        className="text-[#be001e] hover:text-[#be001e]/80 cursor-pointer p-0.5"
                                      >
                                        {isCompanySearchLoading ? (
                                          <Loader2 className="size-3.5 animate-spin text-[#be001e]" />
                                        ) : (
                                          <X className="size-3.5 text-[#be001e]" />
                                        )}
                                      </button>
                                    )}
                                  </div>

                                  {isCompanySearchDropdownOpen && companySearchResults.length > 0 && (
                                    <>
                                      <div
                                        className="fixed inset-0 z-30"
                                        onClick={() => setIsCompanySearchDropdownOpen(false)}
                                      />
                                      <div className="absolute left-0 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg py-1.5 z-40 max-h-[180px] overflow-y-auto scrollbar-thin">
                                        {companySearchResults.map((comp: any) => (
                                          <button
                                            key={comp.id}
                                            type="button"
                                            onClick={() => {
                                              const selectedName = comp.name || comp.legalName || comp.id
                                              isCompanySelectionRef.current = true
                                              setSelectedCompanyId(comp.id)
                                              setCompanyDisplayName(selectedName)
                                              setCompanySearchQuery(selectedName)
                                              setCompanySearchResults([])
                                              setCompanySearchEmpty(false)
                                              setIsCompanySearchLoading(false)
                                              setIsCompanySearchDropdownOpen(false)
                                            }}
                                            className={cn(
                                              "w-full text-left px-3 py-1.5 text-xs transition-colors hover:bg-[#be001e]/5 hover:text-[#be001e] cursor-pointer block font-semibold",
                                              selectedCompanyId === comp.id ? "text-[#be001e] bg-[#be001e]/5" : "text-slate-700"
                                            )}
                                          >
                                            <div className="flex items-center justify-between">
                                              <span>{comp.name || comp.legalName}</span>
                                              {comp.city && (
                                                <span className="text-[10px] text-slate-400 font-normal">
                                                  {comp.city}{comp.country ? `, ${comp.country}` : ""}
                                                </span>
                                              )}
                                            </div>
                                          </button>
                                        ))}
                                      </div>
                                    </>
                                  )}

                                  {isCompanySearchDropdownOpen && companySearchEmpty && (
                                    <div className="absolute left-0 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg py-2 z-40 flex flex-col items-center gap-1.5">
                                      <p className="text-[11px] font-bold text-slate-500">No company found</p>
                                    </div>
                                  )}

                                  {!selectedCompanyId && formError && (
                                    <p className="text-[11px] text-[#be001e] font-semibold mt-1">
                                      Please search and select a target company.
                                    </p>
                                  )}
                                </div>
                              ) : (
                                /* Way 2: Pre-selected / RFQ-Specific Companies (CLOSE RFQ - kept as is) */
                                <>
                                  {loadingCompanies ? (
                                    <div className="flex items-center gap-2 text-xs text-slate-500 py-1.5 font-medium">
                                      <Loader2 className="size-3.5 animate-spin text-[#e6202f]" />
                                      Loading available companies...
                                    </div>
                                  ) : companiesList && companiesList.length > 0 ? (
                                    <>
                                      <select
                                        value={selectedCompanyId}
                                        onChange={(e) => setSelectedCompanyId(e.target.value)}
                                        className={cn(
                                          "w-full h-9 rounded-lg border bg-white px-3 text-xs font-semibold text-slate-700 focus:outline-none cursor-pointer transition-all",
                                          !selectedCompanyId && formError
                                            ? "border-[#be001e] focus:border-[#be001e] ring-1 ring-[#be001e]/10"
                                            : "border-slate-200 focus:border-[#be001e]"
                                        )}
                                        disabled={isSubmitting}
                                      >
                                        <option value="">-- Select a company --</option>
                                        {companiesList.filter((c: any) => c && c.id).map((c: any) => (
                                          <option key={c.id} value={c.id}>
                                            {c.name} {c.city ? `(${c.city}, ${c.country})` : ""}
                                          </option>
                                        ))}
                                      </select>
                                      {!selectedCompanyId && formError && (
                                        <p className="text-[11px] text-[#be001e] font-semibold mt-1">
                                          Please select a target company.
                                        </p>
                                      )}
                                    </>
                                  ) : (
                                    <div className="flex items-center gap-2 text-xs font-semibold text-[#e6202f] bg-red-50 p-2.5 rounded-lg border border-red-100">
                                      <AlertCircle className="size-4 shrink-0" />
                                      <span>No companies available for this RFQ.</span>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>

                            <div className="space-y-1 text-left" id="forward-target-quantity">
                              <label className="text-[9.5px] font-extrabold uppercase tracking-wider text-slate-400">Target Quantity</label>
                              <div className={cn(
                                "flex items-center h-9 rounded-lg border bg-white transition-all shadow-sm overflow-visible pr-1",
                                (quantityError || unitError)
                                  ? "border-[#be001e] focus-within:border-[#be001e] ring-1 ring-[#be001e]/10"
                                  : "border-slate-200 focus-within:border-[#be001e]"
                              )}>
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  value={quantity === "" ? "" : quantity}
                                  onChange={(e) => {
                                    const clean = e.target.value.replace(/\D/g, "")
                                    setQuantity(clean === "" ? "" : Number(clean))
                                  }}
                                  onKeyDown={(e) => {
                                    if (["-", "+", "e", "E", "."].includes(e.key)) {
                                      e.preventDefault()
                                    }
                                  }}
                                  placeholder="e.g. 500"
                                  className="flex-1 min-w-0 h-full bg-transparent px-3 text-xs outline-none border-0 focus:ring-0 font-semibold text-slate-700 placeholder-slate-400"
                                  disabled={isSubmitting}
                                />
                                <div className="h-4 w-[1px] bg-slate-200 mx-1"></div>
                                <div className="relative shrink-0" ref={unitDropdownRef}>
                                  <button
                                    type="button"
                                    onClick={() => setIsUnitDropdownOpen(!isUnitDropdownOpen)}
                                    title={quantityUnit || "Unit"}
                                    className="h-7 flex items-center justify-between gap-1 px-2.5 rounded hover:bg-slate-100/80 text-xs font-semibold text-slate-700 transition-colors cursor-pointer"
                                    disabled={isSubmitting}
                                  >
                                    <span className="truncate max-w-[70px]">{quantityUnit || "Unit"}</span>
                                    <ChevronDown className="size-3 text-slate-400 shrink-0" />
                                  </button>
                                  {isUnitDropdownOpen && (
                                    <div className="absolute right-0 mt-1.5 w-44 bg-white border border-slate-200 rounded-lg shadow-lg z-50 max-h-[132px] overflow-y-auto py-1 text-xs font-semibold text-slate-700 text-left">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setQuantityUnit("")
                                          setIsUnitDropdownOpen(false)
                                        }}
                                        className="w-full text-left px-3 py-1.5 hover:bg-slate-50 text-slate-400 font-bold border-b border-slate-100 transition-colors cursor-pointer"
                                      >
                                        Unit
                                      </button>
                                      {QUANTITY_UNITS.map((unit) => (
                                        <button
                                          key={unit.code}
                                          type="button"
                                          onClick={() => {
                                            setQuantityUnit(unit.code)
                                            setIsUnitDropdownOpen(false)
                                          }}
                                          title={unit.name}
                                          className={cn(
                                            "w-full text-left px-3 py-1.5 hover:bg-slate-50 transition-colors cursor-pointer truncate",
                                            quantityUnit === unit.code ? "text-[#be001e] bg-red-50/30 font-bold" : ""
                                          )}
                                        >
                                          {unit.name}
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                              {(quantityError || unitError) && (
                                <p className="text-[11px] text-[#be001e] font-semibold mt-1">
                                  {quantityError || unitError}
                                </p>
                              )}
                            </div>

                            <div className="space-y-1 text-left">
                              <label className="text-[9.5px] font-extrabold uppercase tracking-wider text-slate-400">
                                Target Sourcing Country
                              </label>
                              <input
                                type="text"
                                value={country}
                                onChange={(e) => setCountry(e.target.value)}
                                placeholder="e.g. India, Germany"
                                className="w-full h-9 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 placeholder-slate-400 focus:border-[#be001e] focus:outline-none"
                                disabled={isSubmitting}
                              />
                            </div>

                            <div className="space-y-1 text-left">
                              <label className="text-[9.5px] font-extrabold uppercase tracking-wider text-slate-400">
                                Lead Time
                              </label>
                              <DatePicker
                                value={leadTime ? leadTime.split("T")[0] : ""}
                                min={new Date().toISOString().split("T")[0]}
                                onChange={setLeadTime}
                                placeholder="Select delivery target date"
                              />
                            </div>

                            <div className="space-y-1 text-left" id="forward-target-budget">
                              <label className="text-[9.5px] font-extrabold uppercase tracking-wider text-slate-400">Target Budget</label>
                              <div className={cn(
                                "flex items-center h-9 rounded-lg border bg-white transition-all shadow-sm overflow-visible pr-1",
                                (budgetError || currencyError)
                                  ? "border-[#be001e] focus-within:border-[#be001e] ring-1 ring-[#be001e]/10"
                                  : "border-slate-200 focus-within:border-[#be001e]"
                              )}>
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  value={budget}
                                  onChange={(e) => setBudget(e.target.value.replace(/\D/g, ""))}
                                  onKeyDown={(e) => {
                                    if (["-", "+", "e", "E", "."].includes(e.key)) {
                                      e.preventDefault()
                                    }
                                  }}
                                  placeholder="e.g. 350"
                                  className="flex-1 min-w-0 h-full bg-transparent px-3 text-xs outline-none border-0 focus:ring-0 font-semibold text-slate-700 placeholder-slate-400"
                                  disabled={isSubmitting}
                                />
                                <div className="h-4 w-[1px] bg-slate-200 mx-1"></div>
                                <div className="relative shrink-0" ref={budgetCurrencyDropdownRef}>
                                  <button
                                    type="button"
                                    onClick={() => setIsBudgetCurrencyDropdownOpen(!isBudgetCurrencyDropdownOpen)}
                                    title={budgetCurrency || "Currency"}
                                    className="h-7 flex items-center justify-between gap-1 px-2.5 rounded hover:bg-slate-100/80 text-xs font-semibold text-slate-700 transition-colors cursor-pointer"
                                    disabled={isSubmitting}
                                  >
                                    <span className="truncate max-w-[70px]">{budgetCurrency || "Currency"}</span>
                                    <ChevronDown className="size-3 text-slate-400 shrink-0" />
                                  </button>
                                  {isBudgetCurrencyDropdownOpen && (
                                    <div className="absolute right-0 mt-1.5 w-48 bg-white border border-slate-200 rounded-lg shadow-lg z-50 max-h-[132px] overflow-y-auto py-1 text-xs font-semibold text-slate-700 text-left">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setBudgetCurrency("")
                                          setIsBudgetCurrencyDropdownOpen(false)
                                        }}
                                        className="w-full text-left px-3 py-1.5 hover:bg-slate-50 text-slate-400 font-bold border-b border-slate-100 transition-colors cursor-pointer"
                                      >
                                        Currency
                                      </button>
                                      {CURRENCIES.map((curr) => (
                                        <button
                                          key={curr.code}
                                          type="button"
                                          onClick={() => {
                                            setBudgetCurrency(curr.code)
                                            setIsBudgetCurrencyDropdownOpen(false)
                                          }}
                                          title={curr.name}
                                          className={cn(
                                            "w-full text-left px-3 py-1.5 hover:bg-slate-50 transition-colors cursor-pointer truncate",
                                            budgetCurrency === curr.code ? "text-[#be001e] bg-red-50/30 font-bold" : ""
                                          )}
                                        >
                                          {curr.name}
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                              {(budgetError || currencyError) && (
                                <p className="text-[11px] text-[#be001e] font-semibold mt-1">
                                  {budgetError || currencyError}
                                </p>
                              )}
                            </div>

                            <div className="space-y-1 text-left md:col-span-2">
                              <label className="text-[9.5px] font-extrabold uppercase tracking-wider text-slate-400">
                                Payment Terms
                              </label>
                              <input
                                type="text"
                                value={paymentTerms}
                                onChange={(e) => setPaymentTerms(e.target.value)}
                                placeholder="e.g. 30% advance, 70% against BL copy"
                                className="w-full h-9 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 placeholder-slate-400 focus:border-[#be001e] focus:outline-none"
                                disabled={isSubmitting}
                              />
                            </div>

                            <div className="space-y-1 text-left md:col-span-2">
                              <label className="text-[9.5px] font-extrabold uppercase tracking-wider text-slate-400">
                                Delivery / Shipping Address
                              </label>
                              <textarea
                                value={address}
                                onChange={(e) => setAddress(e.target.value)}
                                placeholder="Enter complete shipping destination address..."
                                className="w-full min-h-[50px] max-h-[100px] rounded-lg border border-slate-200 bg-white p-2.5 text-xs text-slate-800 placeholder-slate-400 focus:border-[#be001e] focus:outline-none transition-all"
                                disabled={isSubmitting}
                              />
                            </div>

                            <div className="space-y-1 text-left md:col-span-2" id="forward-procurement-request">
                              <label className="text-[9.5px] font-extrabold uppercase tracking-wider text-slate-400">
                                Procurement Request / Forward Details
                              </label>
                              <textarea
                                value={editedMessage}
                                onChange={(e) => setEditedMessage(e.target.value)}
                                placeholder="Enter message to forward to seller..."
                                className={cn(
                                  "w-full min-h-[70px] max-h-[140px] rounded-lg border bg-white p-2.5 text-xs text-slate-800 placeholder-slate-400 focus:outline-none transition-all duration-200",
                                  !editedMessage.trim() && formError
                                    ? "border-[#be001e] focus:border-[#be001e] ring-1 ring-[#be001e]/10"
                                    : "border-slate-200 focus:border-[#be001e]"
                                )}
                                disabled={isSubmitting}
                                required
                              />
                              {!editedMessage.trim() && formError && (
                                <p className="text-[11px] text-[#be001e] font-semibold mt-1">
                                  Procurement Request is required.
                                </p>
                              )}
                            </div>
                          </div>
                        )}

                        <div className="flex gap-2.5 justify-end pt-1">
                          <button
                            type="button"
                            onClick={() => {
                              if (rfq.type?.toUpperCase() === "OPEN" && formAction) {
                                setFormAction(null)
                                companyAbortRef.current?.abort()
                                setSelectedCompanyId("")
                                setCompanySearchQuery("")
                                setCompanyDisplayName("")
                                setCompanySearchResults([])
                                setCompanySearchEmpty(false)
                                setIsCompanySearchLoading(false)
                                setIsCompanySearchDropdownOpen(false)
                              } else {
                                onBack()
                              }
                            }}
                            disabled={isSubmitting}
                            className="px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-lg cursor-pointer border border-slate-200 transition-colors disabled:opacity-50"
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            disabled={isSubmitting || (!selectedCompanyId && formAction !== "revert")}
                            className="px-3 py-1.5 bg-[#be001e] hover:bg-[#e6202f] text-white text-xs font-bold rounded-lg cursor-pointer flex items-center gap-1.5 transition-colors disabled:opacity-50"
                          >
                            {isSubmitting ? (
                              <>
                                <Loader2 className="size-3 animate-spin" />
                                {formAction === "revert" ? "Reverting..." : "Forwarding..."}
                              </>
                            ) : (
                              <>{formAction === "revert" ? "Revert RFQ" : "Forward RFQ"}</>
                            )}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <RfqStatusUpdatesTimeline
            isLoading={activeTab === "buyers" && isStatusUpdatesLoading}
            checkpoints={activeTab === "buyers" ? statusUpdatesData?.checkpoints : []}
            createdAt={rfq.createdAt}
            validUntil={rfq.validUntil}
            createdLabel={activeTab === "buyers" ? "RECEIVED DATE" : "SENT DATE"}
          />
        </div>
      </div>

      {/* Empty space on mobile only when forward or revert form is active so user can scroll above keyboard */}
      {isFormActive && (
        <div className="h-16 sm:h-8 md:hidden pointer-events-none" aria-hidden="true" />
      )}

      {selectedVariant && (
        <VariantDetailSidebar
          variant={selectedVariant}
          onClose={() => setSelectedVariant(null)}
          onCompanyClick={(c) => setSelectedCompany(c)}
        />
      )}
      {selectedProduct && (
        <ProductDetailSidebar
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          onCompanyClick={(c) => setSelectedCompany(c)}
        />
      )}
      {selectedCompany && (
        <CompanyDetailSidebar 
          company={selectedCompany} 
          onClose={() => setSelectedCompany(null)} 
          level={selectedProduct || selectedVariant ? 1 : 0} 
        />
      )}
      {selectedExternalSeller && (
        <ExternalSellerDetailSidebar 
          externalSeller={selectedExternalSeller} 
          onClose={() => setSelectedExternalSeller(null)} 
        />
      )}
    </div>
  )
}

export function RfqDetailsPage({ rfqId, subTab }: { rfqId: string; subTab: "buyers" | "sellers" }) {
  const navigate = useNavigate()
  const activeTab = subTab

  const {
    data: buyerDetailData,
    isLoading: isBuyerDetailLoading,
    isError: isBuyerDetailError,
    error: buyerDetailError,
  } = useGetRfqBuyerToAdminById(
    (activeTab === "buyers" && rfqId) ? rfqId : ""
  )

  const {
    data: sellerDetailData,
    isLoading: isSellerDetailLoading,
    isError: isSellerDetailError,
    error: sellerDetailError,
  } = useGetRfqAdminToSellerById(
    (activeTab === "sellers" && rfqId) ? rfqId : "",
    activeTab === "sellers" && !!rfqId
  )

  const selectedRfq = activeTab === "buyers"
    ? (buyerDetailData?.buyerToAdminRfq || null)
    : (sellerDetailData?.adminToSellerRfq || (sellerDetailData && !sellerDetailData.adminToSellerRfq && sellerDetailData.id ? sellerDetailData : null))

  const isDetailLoading = activeTab === "buyers" ? isBuyerDetailLoading : isSellerDetailLoading
  const isDetailError = activeTab === "buyers" ? isBuyerDetailError : isSellerDetailError
  const detailError = activeTab === "buyers" ? buyerDetailError : sellerDetailError

  const handleGoBack = () => {
    navigate({ to: "/rfqs", search: { subTab } })
  }

  const triggerToast = (msg: string) => {
    toast.success(msg)
  }

  if (isDetailLoading) {
    return (
      <div className="relative flex flex-col items-center justify-center py-20 text-slate-400 gap-3 bg-white border border-[#be001e]/20 rounded-2xl shadow-sm max-w-5xl mx-auto font-sans text-left animate-in fade-in duration-200">
        <button
          onClick={handleGoBack}
          className="absolute top-4 left-4 flex items-center justify-center p-2 rounded-lg border border-[#be001e]/20 bg-white hover:bg-[#be001e]/5 hover:text-[#be001e] hover:shadow-sm transition-all cursor-pointer text-slate-800"
          title="Back to RFQ Queue"
        >
          <ChevronLeft className="size-4" />
        </button>
        <Loader2 className="size-8 animate-spin text-[#e6202f]" />
        <p className="text-sm font-medium text-slate-500 font-sans">Fetching RFQ details...</p>
      </div>
    )
  }

  if (isDetailError) {
    return (
      <div className="max-w-5xl mx-auto space-y-4 font-sans text-left animate-in fade-in duration-200">
        <div className="flex items-center gap-3">
          <button
            onClick={handleGoBack}
            className="flex items-center justify-center p-2 rounded-lg border border-[#be001e]/20 bg-white hover:bg-[#be001e]/5 hover:text-[#be001e] hover:shadow-sm transition-all cursor-pointer text-slate-800"
            title="Back to RFQ Queue"
          >
            <ChevronLeft className="size-4" />
          </button>
          <span className="text-sm font-bold text-slate-700 font-sans">Back to List</span>
        </div>
        <div className="flex items-center gap-3 rounded-2xl border border-red-100 bg-red-50 p-5 text-sm text-[#e6202f]">
          <AlertCircle className="size-5 shrink-0" />
          <p className="font-semibold font-sans">{(detailError as Error)?.message || "Failed to load RFQ details."}</p>
        </div>
      </div>
    )
  }

  if (!selectedRfq) {
    return (
      <div className="max-w-5xl mx-auto space-y-4 font-sans text-left animate-in fade-in duration-200">
        <div className="flex items-center gap-3">
          <button
            onClick={handleGoBack}
            className="flex items-center justify-center p-2 rounded-lg border border-[#be001e]/20 bg-white hover:bg-[#be001e]/5 hover:text-[#be001e] hover:shadow-sm transition-all cursor-pointer text-slate-800"
            title="Back to RFQ Queue"
          >
            <ChevronLeft className="size-4" />
          </button>
          <span className="text-sm font-bold text-slate-700 font-sans">Back to List</span>
        </div>
        <div className="flex items-center gap-3 rounded-2xl border border-yellow-100 bg-yellow-50 p-5 text-sm text-yellow-700">
          <AlertCircle className="size-5 shrink-0" />
          <p className="font-semibold font-sans">RFQ details not found.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4 relative">
      <RfqDetailView
        rfq={selectedRfq}
        activeTab={activeTab}
        onBack={handleGoBack}
        onForwardSuccess={triggerToast}
      />
    </div>
  )
}
